
import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { Box, Divider, Stack, Typography } from "@mui/material";
import htmlParse from "html-react-parser";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import FormCheckbox from "../forms/form-checkbox/FormCheckbox";
import Form from "../forms/form/Form";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";
import { SetAsDefaultFieldType, SetAsDefaultValuesType } from "./Models";
export type SetAsDefaultDialogResult = DialogResult<SetAsDefaultValuesType>;
export type SetAsDefaultDialogProps = DialogProps<SetAsDefaultValuesType, SetAsDefaultDialogResult>;

export default function SetAsDefaultDialog({
    open,
    onClose,
    values,

}: SetAsDefaultDialogProps) {

    const { t } = useTranslation();
    const { confirm } = useConfirmation();

    const form = useForm<SetAsDefaultFieldType>({
        mode: "onChange",
        defaultValues: {
            forCurrentUser: values?.forCurrentUser ?? false,
            forUsersWithoutDefault: values?.forUsersWithoutDefault ?? false,
            forAllUsers: values?.forAllUsers ?? false,
            forAllObjectsInCurrentGroupWithoutDefault: values?.forAllObjectsInCurrentGroupWithoutDefault ?? false
        } as SetAsDefaultFieldType
    });

    const {
        handleSubmit,
        formState: {
            isDirty,
            isValid,
            dirtyFields
        },
        reset,
        setValue
    } = form;

    async function onSubmit(data: SetAsDefaultFieldType) {

        const dialogValues: SetAsDefaultValuesType = {
            ...values as SetAsDefaultValuesType,
            forCurrentUser: data.forCurrentUser,
            forUsersWithoutDefault: data.forUsersWithoutDefault,
            forAllUsers: data.forAllUsers,
            forAllObjectsInCurrentGroupWithoutDefault: data.forAllObjectsInCurrentGroupWithoutDefault
        };

        // Check to see if any options were deselected
        if ((!data.forUsersWithoutDefault && dirtyFields.forUsersWithoutDefault) ||
            (!data.forAllUsers && dirtyFields.forAllUsers) ||
            (!data.forAllObjectsInCurrentGroupWithoutDefault && dirtyFields.forAllObjectsInCurrentGroupWithoutDefault)) {

            if ((await confirm({
                dialogTitle: t("configuration.removeDefaultsConfirmation.title"),
                confirmationText: t("configuration.removeDefaultsConfirmation.text"),
                yesButtonText: t("configuration.removeDefaultsConfirmation.yesButtonText"),
                noButtonText: t("configuration.removeDefaultsConfirmation.noButtonText"),
                showConfirmationText: true,
                showYesButton: true,
                showNoButton: true
            })) !== "yes") {

                return;
            }
        }

        reset();

        onClose && onClose({
            reason: "ok",
            values: dialogValues
        });
    }

    async function onCancel() {
        const canCancel = isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            reset();
            onClose && onClose({
                reason: "cancel"
            });
        }
    }

    async function getUserConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("configuration.setAsDefaultDialog.cancelConfirmation"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    useEffect(() => {

        reset({
            forCurrentUser: values?.forCurrentUser ?? false,
            forUsersWithoutDefault: values?.forUsersWithoutDefault ?? false,
            forAllUsers: values?.forAllUsers ?? false,
            forAllObjectsInCurrentGroupWithoutDefault: values?.forAllObjectsInCurrentGroupWithoutDefault ?? false
        });
    }, [
        reset,
        values
    ]);

    const targetName = values?.objectType === eLynxObjectTypeEnum.Group
        ? values.groupName
        : values?.objectName;

    function onCheckboxChange(checkboxName: string, checked: boolean) {
        if (checked) {
            if (checkboxName === "forAllUsers") {
                setValue("forCurrentUser", false, { shouldDirty: true });
                setValue("forUsersWithoutDefault", false, { shouldDirty: true });
                setValue("forAllObjectsInCurrentGroupWithoutDefault", false, { shouldDirty: true });
            } else {
                setValue("forAllUsers", false, { shouldDirty: true });
            }
        }
    }

    return (
        <>
            <ResponsiveDialog
                title={t("configuration.setAsDefaultDialog.title")}
                enableOkay={isDirty && isValid}
                okayLabel={t("buttons.apply")}
                open={open}
                onClose={
                    (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => onSubmit(data))()
                            : onCancel();
                    }
                }
            >
                <Box>
                    <Typography variant="subtitle1">
                        {htmlParse(t("configuration.setAsDefaultDialog.subtitle", {
                            configurationName: values?.configurationName ?? "Unknown",
                            configurationType: values?.configurationType ?? "Unknown"
                        }))}
                    </Typography>

                    <Form form={form}>
                        <Stack spacing={1}>

                            <Divider />

                            <Typography
                                sx={{
                                    fontWeight: "fontWeightBold",
                                    marginTop: 2
                                }}>
                                {t("configuration.setAsDefaultDialog.heading1", {
                                    objectTypeName: getObjectTypeName(values?.objectType, false, true),
                                    targetName: targetName ?? "Unknown"
                                })}
                            </Typography>

                            <Box>
                                <FormCheckbox
                                    name="forCurrentUser"
                                    label={t("configuration.setAsDefaultDialog.option1")}
                                    onChange={
                                        (_event, checked) => onCheckboxChange("forCurrentUser", checked)
                                    }
                                />
                            </Box>

                            <Box>
                                <FormCheckbox
                                    name="forUsersWithoutDefault"
                                    label={t("configuration.setAsDefaultDialog.option2")}
                                    onChange={
                                        (_event, checked) => onCheckboxChange("forUsersWithoutDefault", checked)
                                    }
                                />
                            </Box>

                            <Box>
                                <FormCheckbox
                                    name="forAllUsers"
                                    label={t("configuration.setAsDefaultDialog.option3")}
                                    onChange={
                                        (_event, checked) => onCheckboxChange("forAllUsers", checked)
                                    }
                                />
                            </Box>

                            {values?.objectType !== eLynxObjectTypeEnum.Group &&
                                <>
                                    <Divider />

                                    <Typography sx={{
                                        fontWeight: "fontWeightBold"
                                    }}>
                                        {t("configuration.setAsDefaultDialog.heading2", {
                                            objectTypeName: getObjectTypeName(values?.objectType, true, true),
                                            groupName: values?.groupName ?? "Unknown"
                                        })}
                                    </Typography>

                                    <Box>
                                        <FormCheckbox
                                            name="forAllObjectsInCurrentGroupWithoutDefault"
                                            label={t("configuration.setAsDefaultDialog.option4")}
                                            onChange={
                                                (_event, checked) => onCheckboxChange("forAllObjectsInCurrentGroupWithoutDefault", checked)
                                            }
                                        />
                                    </Box>
                                </>
                            }
                        </Stack>
                    </Form>
                </Box>
            </ResponsiveDialog>
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserConfirmation}
            />
        </>
    );
}

export type DefaultConfigurationDialogState = DialogState<SetAsDefaultValuesType, SetAsDefaultDialogResult>;
export type BindDefaultConfigurationDialogReturn = BindDialogReturn<SetAsDefaultValuesType, SetAsDefaultDialogResult>;

export function useDefaultConfigurationDialogState(): DefaultConfigurationDialogState {
    return useDialogState();
}

export function bindDefaultConfigurationDialog(props: DefaultConfigurationDialogState): BindDefaultConfigurationDialogReturn {
    return bindDialog(props);
}